import { ColDef, ColumnState } from '@ag-grid-community/core';
import round from 'lodash/round';
import { NavLink } from 'react-router-dom';
import { agTableValueComparator } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { AG_TABLE_RIGHT_ALIGNED_COLUMN_DEF, EM_DASH } from '../../../../Base.constants';
import { getCompanyPageLink } from '../../../../pages/Companies/Companies.logic';
import {
  RecentChangeListItemDto,
  RecentChangeMetric,
} from '../../../../services/dtos/riskProducts/dashboard/recentChangeList.dto';
import { DATE_FORMATS, formatDate } from '../../../../utils/dateUtils';
import { getCompanyTag } from '../../../../utils/tagUtils';
import MetricChangeIcon from '../../common/MetricChangeIcon/MetricChangeIcon';
import { formatAsPctString } from '../../common/MetricChangeIcon/MetricChangeIcon.logic';
import styles from './RecentChange.module.scss';

const cx = classNames.bind(styles);

const isCreMetric = (metric: string): boolean => {
  return metric === RecentChangeMetric.CRE;
};

const formatToMetric = (metric: string, value: number): string => {
  let result: string = '';

  if (isCreMetric(metric)) {
    result = formatAsPctString(value, 2);
  } else {
    const roundedValue = round(value, 0);
    result = roundedValue.toString();
  }
  return result;
};

const metricValueGet = (metric: string, data: any): number | null => {
  let value: number | null = null;
  if (isCreMetric(metric)) {
    if (data?.cre_1y) {
      value = data.cre_1y;
    }
  } else if (data?.cqs) {
    value = data.cqs;
  }
  return value;
};

const metricPreviousGet = (metric: string, data: any): number | null => {
  let value: number | null = null;
  if (isCreMetric(metric)) {
    if (data?.cre_previous) {
      value = data.cre_previous;
    }
  } else if (data?.cqs_previous) {
    value = data.cqs_previous;
  }
  return value;
};

const metricChangeGet = (metric: string, data: any): number | null => {
  let result: number | null = null;
  const metricValue: number | null = metricValueGet(metric, data);
  const metricPrevious: number | null = metricPreviousGet(metric, data);
  if (metricValue && metricPrevious) {
    result = metricValue - metricPrevious;
  }
  return result;
};

export const getRecentChangesColumnDef = (
  metric: string,
  currentColumnState?: ColumnState[]
): ColDef<RecentChangeListItemDto>[] => {
  // collect current column widths and re-use them to avoid table flickering when changing metric
  const columnWidths = (currentColumnState ?? []).reduce((acc, state) => {
    acc[state.colId] = state.width;
    return acc;
  }, {} as Record<string, number | undefined>);

  return [
    {
      colId: 'name',
      field: 'name',
      headerName: 'Company',
      width: columnWidths.name || 223,
      pinned: 'left',
      cellRenderer: (params) => (
        <NavLink to={getCompanyPageLink(getCompanyTag(params.data.cs_companyid))}>{params.data.display_name}</NavLink>
      ),
      suppressMenu: true,
      resizable: false,
      suppressMovable: true,
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const nameA = nodeA.data?.display_name;
        const nameB = nodeB.data?.display_name;
        let res = 0;
        if (typeof nameA !== 'undefined' && typeof nameB !== 'undefined') {
          res = nameA.localeCompare(nameB);
        }
        return res;
      },
    },
    {
      colId: 'metric',
      width: columnWidths.metric,
      headerName: metric === RecentChangeMetric.CRE ? 'CRE (%)' : 'CQS',
      ...AG_TABLE_RIGHT_ALIGNED_COLUMN_DEF,
      cellRenderer: ({ data, valueFormatted }) => {
        const value = metricValueGet(metric, data);
        const previousValue = metricPreviousGet(metric, data);

        return (
          <MetricChangeIcon
            displayValue={valueFormatted}
            value={value !== null ? round(value, 4) : value}
            previousValue={previousValue !== null ? round(previousValue, 4) : previousValue}
          />
        );
      },
      valueFormatter: (params) => {
        const value = metricValueGet(metric, params.data);
        if (value == null) {
          return '';
        }
        return formatToMetric(metric, value);
      },
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const value1 = metricValueGet(metric, nodeA.data) as number;
        const value2 = metricValueGet(metric, nodeB.data) as number;

        return agTableValueComparator(formatToMetric(metric, value1), formatToMetric(metric, value2));
      },
      suppressMenu: true,
      resizable: false,
      suppressMovable: true,
      headerClass: cx('custom-header-align-right'),
    },
    {
      colId: 'metric_previous',
      width: columnWidths.metric,
      headerName: metric === RecentChangeMetric.CRE ? 'CRE Prev (%)' : 'CQS Prev',
      ...AG_TABLE_RIGHT_ALIGNED_COLUMN_DEF,
      cellRenderer: ({ data, valueFormatted }) => (
        <MetricChangeIcon displayValue={valueFormatted} value={metricPreviousGet(metric, data)} />
      ),
      valueFormatter: (params) => {
        const value = metricPreviousGet(metric, params.data);
        if (value == null) {
          return '';
        }
        return formatToMetric(metric, value);
      },
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const value1 = metricPreviousGet(metric, nodeA.data);
        const value2 = metricPreviousGet(metric, nodeB.data);

        return agTableValueComparator(value1, value2);
      },
      suppressMenu: true,
      resizable: false,
      suppressMovable: true,
      headerClass: cx('custom-header-align-right'),
    },
    {
      colId: 'metric_change',
      width: columnWidths.metric,
      headerName: metric === RecentChangeMetric.CRE ? 'CRE Chg (bp)' : 'CQS Chg',
      ...AG_TABLE_RIGHT_ALIGNED_COLUMN_DEF,
      cellRenderer: ({ data, valueFormatted }) => (
        <MetricChangeIcon displayValue={valueFormatted} value={metricChangeGet(metric, data)} />
      ),
      valueFormatter: (params) => {
        const value: number | null = metricChangeGet(metric, params.data);
        if (value == null) {
          return '';
        }
        if (isCreMetric(metric)) {
          return round(value * 10000, 0).toString();
        }

        return round(value, 2).toString();
      },
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const value1 = metricChangeGet(metric, nodeA.data);
        const value2 = metricChangeGet(metric, nodeB.data);

        return agTableValueComparator(value1, value2);
      },
      suppressMenu: true,
      resizable: false,
      suppressMovable: true,
      headerClass: cx('custom-header-align-right'),
    },
    {
      colId: 'calc_date',
      width: columnWidths.calc_date,
      valueFormatter: (params) => formatDate(params.data?.calc_date, DATE_FORMATS.MONTH_ABBR_DATE, EM_DASH),
      headerName: 'Date',
      suppressMenu: true,
      resizable: false,
      suppressMovable: true,
      comparator: (valueA, valueB, nodeA, nodeB) => {
        return agTableValueComparator(nodeA?.data?.calc_date, nodeB?.data?.calc_date);
      },
    },
  ];
};
